import axios from "axios";

/**
 * Dedicated Axios Client Instances
 * Separate instances for:
 * 1. HelloTrade backend (REACT_APP_HELLOTRADE_API_BASE_URL)
 * 2. Loans backend (REACT_APP_BACKEND_PATH)
 * 3. OTP MS backend (HT_OTP_MS)
 * Preserves exact endpoint paths without auto-prefixing or altering /v1.
 *
 * NOTE on response contract:
 *   The three named clients resolve to the FULL axios response (so callers
 *   read response.data...), matching the call-site migration pattern.
 *   The DEFAULT export preserves the pre-existing contract (resolves to
 *   response.data) so existing importers (goldLoanService, personalLoanService)
 *   keep working unchanged while call sites are migrated incrementally.
 */

const getHelloTradeBaseURL = () => {
  return (
    process.env.REACT_APP_HELLOTRADE_API_BASE_URL ||
    "https://www.hellotrade.com/api"
  ).replace(/\/$/, "");
};

const getLoansBaseURL = () => {
  return (
    process.env.REACT_APP_BACKEND_PATH ||
    "https://dev-loans.indiamart.com/api"
  ).replace(/\/$/, "");
};

const getOtpMsBaseURL = () => {
  return (process.env.REACT_APP_HT_OTP_MS || process.env.HT_OTP_MS || "").replace(/\/$/, "");
};

// 1. HelloTrade Backend Axios Instance
export const helloTradeClient = axios.create({
  baseURL: getHelloTradeBaseURL(),
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

// 2. Loans Backend Axios Instance
export const loansClient = axios.create({
  baseURL: getLoansBaseURL(),
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

// 3. OTP MS Backend Axios Instance
export const otpMsClient = axios.create({
  baseURL: getOtpMsBaseURL(),
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Response Interceptor for Error Logging (full-response contract)
const handleResponse = (response) => response;
const handleError = (error) => {
  console.error("API Error:", error?.response?.status, error?.message);
  return Promise.reject(error);
};

helloTradeClient.interceptors.response.use(handleResponse, handleError);
loansClient.interceptors.response.use(handleResponse, handleError);
otpMsClient.interceptors.response.use(handleResponse, handleError);

// Backward-compatible default export: single client that resolves to
// response.data, preserving the original axiosClient contract.
const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_PATH,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

axiosClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    console.error("API error:", error);
    return Promise.reject(error);
  }
);

export default axiosClient;
